import { useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/errors';
import { Route42InlineMarkdown, Route42Markdown } from '../../../components/ui/Route42Markdown';
import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useDocuments } from '../../documents/api/useDocuments';
import { createInvestigation } from '../../investigations/api/investigations.api';
import { StartInvestigationDialog } from '../../investigations/components/StartInvestigationDialog';
import { useMachines } from '../../machines/api/useMachines';
import { useMachineMemory } from '../api/useMachineMemory';
import { MemoryDetailPanel, memoryQuestionFor } from '../components/MemoryDetailPanel';
import type {
  MachineMemoryConflictDto,
  MachineMemoryGapDto,
  MachineMemoryInsightDto,
  MachineMemoryInvestigationKnowledgeDto,
  MachineMemoryItem,
  MachineMemoryRelationshipDto,
  MachineMemoryThemeDto,
  MachineWorkspaceResponseDto,
} from '../types/machine-memory';
import { asCount, evidenceStrengthLabel, humanMemoryType } from '../types/machine-memory';

export function MachineMemoryPage() {
  const { machineId = '' } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const machines = useMachines();
  const documents = useDocuments(machineId, auth.can(permissions.documentsRead));
  const memory = useMachineMemory(machineId, auth.can(permissions.machineRead));
  const [detail, setDetail] = useState<MachineMemoryItem | null>(null);
  const [investigationDraft, setInvestigationDraft] = useState<{ title: string; objective: string } | null>(null);
  const [investigationBusy, setInvestigationBusy] = useState(false);
  const [investigationError, setInvestigationError] = useState<string | null>(null);
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const learningCount = documents.data?.documents.filter((document) => document.uiStatus === 'learning' || document.uiStatus === 'uploading').length ?? 0;
  const readyCount = documents.data?.documents.filter((document) => document.questionReady).length ?? 0;
  const memoryNotReady = memory.error instanceof ApiError && memory.error.code === 'MACHINE_MEMORY_NOT_READY';
  const canAsk = auth.can(permissions.questionAsk) && auth.can(permissions.conversationCreate) && auth.can(permissions.conversationRead);

  if (!auth.can(permissions.machineRead)) return <Navigate to={`/machines/${machineId}`} replace />;

  async function startInvestigation(title: string, objective: string) {
    setInvestigationBusy(true);
    setInvestigationError(null);
    try {
      const existing = memory.data?.investigations.find((item) => item.status === 'OPEN' && normalize(item.title) === normalize(title));
      if (existing) {
        navigate(`/machines/${machineId}/investigations/${existing.investigation_id}`);
        return;
      }
      const created = await createInvestigation(machineId, title, objective);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.machineMemory(auth.activeCustomerId, machineId) }),
      ]);
      navigate(`/machines/${machineId}/investigations/${created.investigation.investigation_id}`);
    } catch (value) {
      setInvestigationError(value instanceof ApiError ? value.message : 'Route42 could not start this investigation. Please try again.');
    } finally {
      setInvestigationBusy(false);
    }
  }

  function ask(prompt: string) {
    navigate(`/machines/${machineId}/conversations/new?prompt=${encodeURIComponent(prompt)}`);
  }

  function investigate(title: string, objective: string) {
    setDetail(null);
    setInvestigationError(null);
    setInvestigationDraft({ title, objective });
  }

  if (machines.isPending || documents.isPending || memory.isPending) {
    return <MemoryLoading />;
  }

  if (machines.isError || documents.isError) {
    return <MemoryError onRetry={() => void Promise.all([machines.refetch(), documents.refetch(), memory.refetch()])} />;
  }

  if (memory.isError && !memoryNotReady) {
    return <MemoryUnavailable onRetry={() => void memory.refetch()} />;
  }

  if (!memory.data) {
    return (
      <MemoryNotReady
        machineName={machine?.name ?? 'This machine'}
        machineId={machineId}
        documentCount={documents.data?.documents.length ?? 0}
        readyCount={readyCount}
        learningCount={learningCount}
        canAsk={canAsk}
      />
    );
  }

  const workspace = memory.data;
  const understanding = workspace.current_understanding ?? {};
  const insights = sortInsights(understanding.insights ?? []);
  const recurringInsights = insights.filter(isRecurringInsight);
  const keyInsights = insights.filter((item) => !isRecurringInsight(item));
  const relationships = understanding.relationships ?? [];
  const gaps = understanding.documentation_gaps ?? [];
  const conflicts = understanding.known_conflicts ?? [];
  const themes = understanding.recurring_themes ?? [];
  const investigationKnowledge = understanding.investigation_knowledge ?? [];
  const status = intelligenceStatus(workspace, learningCount);

  return (
    <section className="r42-memory-page">
      <header className="r42-memory-head">
        <div>
          <span className="r42-eyebrow">Machine intelligence</span>
          <h1>Machine Memory</h1>
          <p>What Route42 currently knows about {machine?.name ?? workspace.machine.machine_name ?? 'this machine'}.</p>
        </div>
        <div className="r42-memory-head-meta">
          <div className="r42-memory-head-actions">
            <span className={`r42-memory-status r42-memory-status--${status.tone}`}><i className={status.icon} aria-hidden="true" /> {status.label}</span>
            {auth.can(permissions.documentsUpload) ? <Link className="r42-memory-add-link" to={`/machines/${machineId}/documents/add`}>Add information</Link> : null}
          </div>
          <span className="r42-memory-version">Version {workspace.machine_memory.memory_version ?? '—'}</span>
        </div>
      </header>

      <div className="r42-memory-layout">
        <main className="r42-memory-main">
          <section className="r42-memory-assessment">
            <div className="r42-memory-section-head">
              <div><span className="r42-eyebrow">Current assessment</span><h2>What Route42 currently understands</h2></div>
              {canAsk ? <button className="r42-section-action" type="button" onClick={() => ask('Summarize the current Machine Memory and tell me what I should pay attention to.')}>Ask about this</button> : null}
            </div>
            <Route42Markdown className="r42-memory-assessment-text" content={workspace.machine_memory.summary || 'Machine Memory is active, but no integrated assessment text is available yet.'} />
            <div className="r42-memory-assessment-meta">
              <span><i className="bi bi-clock-history" aria-hidden="true" /> Updated {formatDateTime(workspace.machine_memory.updated_at)}</span>
              {learningCount > 0 ? <span><i className="bi bi-arrow-repeat" aria-hidden="true" /> {learningCount} {learningCount === 1 ? 'document is' : 'documents are'} still updating intelligence</span> : null}
            </div>
          </section>

          {investigationKnowledge.length ? (
            <MemorySection eyebrow="Learned from Investigations" title="What the team established and Route42 retained" count={investigationKnowledge.length}>
              <div className="r42-memory-item-list">
                {investigationKnowledge.map((item) => (
                  <MemoryInvestigationKnowledgeRow
                    key={item.record_id}
                    item={item}
                    investigation={workspace.investigations.find((value) => value.investigation_id === item.investigation_id)}
                    onOpen={() => setDetail({ kind: 'INVESTIGATION_KNOWLEDGE', value: item })}
                    onAsk={canAsk ? ask : undefined}
                  />
                ))}
              </div>
            </MemorySection>
          ) : null}

          {themes.length || recurringInsights.length ? (
            <MemorySection eyebrow="Recurring issues" title="Patterns Route42 sees repeatedly" count={themes.length + recurringInsights.length}>
              <div className="r42-memory-item-list">
                {themes.map((item, index) => <MemoryThemeRow key={item.theme_id || `${item.title}-${index}`} item={item} onOpen={() => setDetail({ kind: 'THEME', value: item })} onAsk={canAsk ? ask : undefined} />)}
                {recurringInsights.map((item) => <MemoryInsightRow key={item.insight_id} item={item} onOpen={() => setDetail({ kind: 'INSIGHT', value: item })} onAsk={canAsk ? ask : undefined} />)}
              </div>
            </MemorySection>
          ) : null}

          {relationships.length ? (
            <MemorySection eyebrow="Observed relationships" title="How conditions and events appear together" count={relationships.length}>
              <div className="r42-memory-item-list">
                {relationships.map((item) => <MemoryRelationshipRow key={item.relationship_id} item={item} onOpen={() => setDetail({ kind: 'RELATIONSHIP', value: item })} onAsk={canAsk ? ask : undefined} />)}
              </div>
            </MemorySection>
          ) : null}

          {keyInsights.length ? (
            <MemorySection eyebrow="Key insights" title="Evidence-backed machine intelligence" count={keyInsights.length}>
              <div className="r42-memory-item-list">
                {keyInsights.map((item) => <MemoryInsightRow key={item.insight_id} item={item} onOpen={() => setDetail({ kind: 'INSIGHT', value: item })} onAsk={canAsk ? ask : undefined} />)}
              </div>
            </MemorySection>
          ) : null}

          {gaps.length ? (
            <MemorySection eyebrow="Knowledge gaps" title="What Route42 still cannot establish" count={gaps.length}>
              <div className="r42-memory-item-list">
                {gaps.map((item) => <MemoryGapRow key={item.gap_id} item={item} onOpen={() => setDetail({ kind: 'GAP', value: item })} onAsk={canAsk ? ask : undefined} />)}
              </div>
            </MemorySection>
          ) : null}

          {conflicts.length ? (
            <MemorySection eyebrow="Conflicting information" title="Information that deserves review" count={conflicts.length} tone="attention">
              <div className="r42-memory-item-list">
                {conflicts.map((item) => <MemoryConflictRow key={item.conflict_id} item={item} onOpen={() => setDetail({ kind: 'CONFLICT', value: item })} onAsk={canAsk ? ask : undefined} />)}
              </div>
            </MemorySection>
          ) : null}

          {!investigationKnowledge.length && !themes.length && !insights.length && !relationships.length && !gaps.length && !conflicts.length ? (
            <section className="r42-memory-section"><div className="r42-memory-section-empty">Machine Memory is active, but Route42 has not recorded any structured insights, relationships, gaps, or conflicts yet.</div></section>
          ) : null}
        </main>

        <aside className="r42-memory-context" aria-label="Machine Memory context">
          <section>
            <span className="r42-eyebrow">Source coverage</span>
            <strong>{asCount(workspace.counts.documents)} documents</strong>
            <div className="r42-memory-coverage-grid">
              <div><b>{asCount(workspace.counts.facts)}</b><span>Facts</span></div>
              <div><b>{asCount(workspace.counts.knowledge_chunks)}</b><span>Knowledge chunks</span></div>
              <div><b>{asCount(workspace.counts.insights)}</b><span>Insights</span></div>
              <div><b>{asCount(workspace.counts.relationships)}</b><span>Relationships</span></div>
              <div><b>{asCount(workspace.counts.machine_knowledge)}</b><span>Investigation learnings</span></div>
            </div>
            <CoverageDetails workspace={workspace} />
            {auth.can(permissions.documentsRead) ? <Link to={`/machines/${machineId}/documents`}>View source documents <i className="bi bi-arrow-right" aria-hidden="true" /></Link> : null}
          </section>

          <section>
            <span className="r42-eyebrow">Attention</span>
            <div className="r42-memory-attention-list">
              <span><b>{gaps.length}</b> knowledge {gaps.length === 1 ? 'gap' : 'gaps'}</span>
              <span><b>{conflicts.length}</b> {conflicts.length === 1 ? 'conflict' : 'conflicts'}</span>
              <span><b>{workspace.investigations.filter((item) => item.status === 'OPEN').length}</b> open {workspace.investigations.filter((item) => item.status === 'OPEN').length === 1 ? 'investigation' : 'investigations'}</span>
            </div>
          </section>

          <section>
            <span className="r42-eyebrow">Memory state</span>
            <p><strong>Machine Intelligence {status.label}</strong></p>
            <p>This describes Route42's knowledge state. It does not indicate physical machine health.</p>
          </section>
        </aside>
      </div>

      {detail ? <MemoryDetailPanel machineId={machineId} item={detail} documents={auth.can(permissions.documentsRead) ? workspace.documents : []} investigations={auth.can(permissions.investigationRead) ? workspace.investigations : []} onAsk={canAsk ? ask : undefined} onInvestigate={auth.can(permissions.investigationCreate) ? investigate : undefined} onClose={() => setDetail(null)} /> : null}
      {investigationDraft ? <StartInvestigationDialog title={investigationDraft.title} objective={investigationDraft.objective} busy={investigationBusy} error={investigationError} onClose={() => { if (!investigationBusy) { setInvestigationDraft(null); setInvestigationError(null); } }} onSubmit={startInvestigation} /> : null}
    </section>
  );
}

function MemorySection({ eyebrow, title, count, tone, children }: { eyebrow: string; title: string; count: number; tone?: 'attention'; children: ReactNode }) {
  return <section className={`r42-memory-section${tone ? ` r42-memory-section--${tone}` : ''}`}><div className="r42-memory-section-head"><div><span className="r42-eyebrow">{eyebrow}</span><h2>{title}</h2></div><span className="r42-memory-count">{count}</span></div>{children}</section>;
}

function MemoryInsightRow({ item, onOpen, onAsk }: { item: MachineMemoryInsightDto; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const title = item.title || humanMemoryType(item.type) || 'Machine insight';
  return <MemoryRow title={title} statement={item.statement} meta={humanMemoryType(item.type)} strength={evidenceStrengthLabel(item.evidence_strength)} onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'INSIGHT', value: item })) : undefined} />;
}
function MemoryInvestigationKnowledgeRow({ item, investigation, onOpen, onAsk }: { item: MachineMemoryInvestigationKnowledgeDto; investigation?: MachineWorkspaceResponseDto['investigations'][number]; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const statement = item.statement || humanMemoryType(item.knowledge_type) || 'Confirmed Investigation learning';
  const source = investigation?.title ? `Learned from ${investigation.title}` : 'Human-confirmed Investigation learning';
  return <MemoryRow title={statement} meta={`${humanMemoryType(item.knowledge_type) || 'Investigation learning'} · ${source}`} onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'INVESTIGATION_KNOWLEDGE', value: item })) : undefined} />;
}
function MemoryThemeRow({ item, onOpen, onAsk }: { item: MachineMemoryThemeDto; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const title = item.title || 'Recurring theme';
  return <MemoryRow title={title} statement={item.statement} meta="Recurring Theme" strength={evidenceStrengthLabel(item.evidence_strength)} onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'THEME', value: item })) : undefined} />;
}
function MemoryRelationshipRow({ item, onOpen, onAsk }: { item: MachineMemoryRelationshipDto; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const title = item.title || humanMemoryType(item.type) || 'Observed relationship';
  const conflicting = Boolean(item.conflicting_evidence && Object.values(item.conflicting_evidence).some((value) => Array.isArray(value) && value.length));
  return <MemoryRow title={title} statement={item.statement} meta={humanMemoryType(item.type)} strength={evidenceStrengthLabel(conflicting ? 'CONFLICTING' : item.evidence_strength)} onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'RELATIONSHIP', value: item })) : undefined} />;
}
function MemoryGapRow({ item, onOpen, onAsk }: { item: MachineMemoryGapDto; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const title = humanMemoryType(item.gap_type) || 'Knowledge gap';
  return <MemoryRow title={title} statement={item.statement} meta="Knowledge Gap" strength="Limited" onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'GAP', value: item })) : undefined} />;
}
function MemoryConflictRow({ item, onOpen, onAsk }: { item: MachineMemoryConflictDto; onOpen: () => void; onAsk?: (prompt: string) => void }) {
  const title = humanMemoryType(item.conflict_type) || 'Conflicting information';
  return <MemoryRow title={title} statement={item.statement} meta="Conflicting Information" strength="Conflicting" onOpen={onOpen} onAsk={onAsk ? () => onAsk(memoryQuestionFor({ kind: 'CONFLICT', value: item })) : undefined} />;
}

function MemoryRow({ title, statement, meta, strength, onOpen, onAsk }: { title: string; statement?: string | null; meta?: string | null; strength?: string | null; onOpen: () => void; onAsk?: () => void }) {
  return (
    <article className="r42-memory-row">
      <button className="r42-memory-row__body" type="button" onClick={onOpen}>
        <span className="r42-memory-row__meta">{meta || 'Machine Memory'}{strength ? <b className={`r42-evidence-strength r42-evidence-strength--${strength.toLocaleLowerCase()}`}>{strength}</b> : null}</span>
        <strong>{title}</strong>
        {statement ? <p><Route42InlineMarkdown content={statement} /></p> : null}
      </button>
      <div className="r42-memory-row__actions">
        {onAsk ? <button type="button" onClick={onAsk}>Ask about this</button> : null}
        <button type="button" aria-label={`View Machine Memory detail for ${title}`} onClick={onOpen}><i className="bi bi-chevron-right" aria-hidden="true" /></button>
      </div>
    </article>
  );
}

function CoverageDetails({ workspace }: { workspace: MachineWorkspaceResponseDto }) {
  const profile = workspace.current_understanding.document_profile ?? {};
  const types = Object.entries(profile.document_types ?? {}).filter(([, count]) => asCount(count) > 0);
  const start = profile.date_coverage?.start;
  const end = profile.date_coverage?.end;
  return <div className="r42-memory-coverage-details">{types.length ? <p>{types.map(([type, count]) => `${asCount(count)} ${humanMemoryType(type)}`).join(' • ')}</p> : null}{start || end ? <p>Coverage: {start || 'Unknown'} → {end || 'Present'}</p> : null}</div>;
}

function MemoryLoading() {
  return <section className="r42-memory-page"><div className="r42-memory-loading" data-testid="machine-memory-loading" role="status"><span className="r42-learning-dot" aria-hidden="true" /><div><strong>Loading Machine Memory…</strong><span>Route42 is assembling the current machine intelligence view.</span></div></div></section>;
}
function MemoryError({ onRetry }: { onRetry: () => void }) {
  return <section className="r42-memory-page"><div className="r42-inline-state r42-inline-state--error"><span>Route42 couldn't load this machine's intelligence context.</span><button className="btn btn-sm btn-outline-secondary" onClick={onRetry}>Try again</button></div></section>;
}
function MemoryUnavailable({ onRetry }: { onRetry: () => void }) {
  return <section className="r42-memory-page"><div className="r42-memory-state"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span className="r42-eyebrow">Machine Intelligence unavailable</span><h1>Machine Memory could not be loaded.</h1><p>The machine and its source documents remain available. This does not indicate a physical machine problem.</p><button className="btn btn-outline-secondary" onClick={onRetry}>Try again</button></div></section>;
}
function MemoryNotReady({ machineName, machineId, documentCount, readyCount, learningCount, canAsk }: { machineName: string; machineId: string; documentCount: number; readyCount: number; learningCount: number; canAsk: boolean }) {
  const noDocuments = documentCount === 0;
  return <section className="r42-memory-page"><div className="r42-memory-state"><i className={noDocuments ? 'bi bi-memory' : 'bi bi-arrow-repeat'} aria-hidden="true" /><span className="r42-eyebrow">Machine Intelligence {noDocuments ? 'limited' : 'updating'}</span><h1>{noDocuments ? `Route42 needs information about ${machineName}.` : `Route42 is building Machine Memory for ${machineName}.`}</h1><p>{noDocuments ? 'Add machine-specific documents to begin building persistent machine intelligence.' : `${readyCount} ${readyCount === 1 ? 'document is' : 'documents are'} already available for grounded questions${learningCount ? `, and ${learningCount} ${learningCount === 1 ? 'document is' : 'documents are'} still processing` : ''}. Machine Memory will appear here once the broader intelligence update completes.`}</p><div className="r42-memory-state-actions">{canAsk ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new`}>{`Ask ${ROUTE42_ASSISTANT_NAME}`}</Link> : null}<Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents`}>{noDocuments ? 'Add information' : 'View documents'}</Link></div></div></section>;
}

function intelligenceStatus(workspace: MachineWorkspaceResponseDto, learningCount: number) {
  if (learningCount > 0) return { label: 'Updating', tone: 'updating', icon: 'bi bi-arrow-repeat' };
  const raw = String(workspace.machine_memory.status ?? '').toLocaleUpperCase();
  if (raw.includes('LIMITED')) return { label: 'Limited', tone: 'limited', icon: 'bi bi-info-circle' };
  if (raw.includes('WARNING') || asCount(workspace.counts.known_conflicts) > 0) return { label: 'Needs attention', tone: 'attention', icon: 'bi bi-exclamation-circle' };
  return { label: 'Current', tone: 'current', icon: 'bi bi-check-circle' };
}
function sortInsights(items: MachineMemoryInsightDto[]) { return [...items].sort((a, b) => Number(a.display_priority ?? 999) - Number(b.display_priority ?? 999)); }
function isRecurringInsight(item: MachineMemoryInsightDto) { return String(item.type ?? '').toLocaleUpperCase().includes('RECURRING') || /recurr|repeated|every quarterly|trend/i.test(item.title ?? ''); }
function formatDateTime(value: string | null | undefined) { if (!value) return 'unknown'; const date = new Date(value); return Number.isNaN(date.valueOf()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date); }
function normalize(value: string | null | undefined) { return String(value ?? '').trim().toLocaleLowerCase(); }
